import { useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { ASSET_GROUPS_QUERY } from '@scio3/graphql-client';
import type { AssetGroup } from '@scio3/shared-types';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useTeamStore } from '@/stores/team-store';

interface GroupsQueryResult {
  groups: {
    page: {
      edges: Array<{ node: AssetGroup }>;
    };
  };
}

export interface UseCurrentGroupResult {
  currentGroup: AssetGroup | null;
  loading: boolean;
  refetch: () => void;
}

/**
 * Fetches the current folder group object for a given path.
 *
 * When the user navigates into a subfolder (e.g., `/FolderA/FolderB`), this hook
 * queries the parent path (`/FolderA`) for groups named `FolderB` to get the
 * current group's security fields (securities, sharedLink).
 *
 * Used by:
 * - Gap 8: Shared folder confirmation (checking if current group has securities)
 * - Gap 3: Permission gating (checking team ownership)
 * - Gap 9: Path validation (wait for loading before redirecting invalid paths)
 *
 * Skips the query when at root path (no group context needed).
 */
export function useCurrentGroup(currentPath: string, folderTeamId?: string): UseCurrentGroupResult {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const effectiveTeamId = useEffectiveTeamId(currentPath, folderTeamId);

  // Parse path to get parent path and folder name
  // e.g., '/FolderA/FolderB' -> parentPath='/FolderA', folderName='FolderB'
  // e.g., '/FolderA' -> parentPath=null (root level), folderName='FolderA'
  // Valid folder paths start with '/' — skip special modes like 'SHARE_WITH_ME'
  const isSubfolder = currentPath !== '/' && currentPath.length > 1 && currentPath.startsWith('/');
  const lastSlash = currentPath.lastIndexOf('/');
  const parentPath = lastSlash > 0 ? currentPath.substring(0, lastSlash) : null;
  const folderName = isSubfolder ? currentPath.substring(lastSlash + 1) : null;

  const shouldSkip = !isSubfolder || !effectiveTeamId || !folderName;
  // Cross-team shared folders: use network-only to prevent showing stale cached data
  // when the user navigates directly to a folder URL after access was revoked.
  // Same-team folders use cache-and-network for faster UX (no revocation risk).
  const isCrossTeam = !!folderTeamId && folderTeamId !== activeTeamId;

  const {
    data,
    loading,
    refetch: apolloRefetch,
  } = useQuery<GroupsQueryResult>(ASSET_GROUPS_QUERY, {
    variables: {
      // Top-level teamId: user's active team (auth context) — matches useScreens pattern.
      // Using effectiveTeamId here caused INVALID_TEAM for cross-team shared folders
      // because the user doesn't belong to the folder's owning team.
      teamId: activeTeamId,
      query: {
        type: 'ASSET',
        // query.teamId: folder's owning team — tells backend which team's folder tree to search.
        // Always include regardless of parentPath — without this, root-level shared folders
        // from other teams can't be found (the backend searches the wrong team), causing
        // the path validation effect to redirect to Home (#279).
        teamId: effectiveTeamId,
        path: parentPath,
        name: folderName,
      },
      // Cross-team: backend $or filter may return a same-name folder from the user's own
      // team alongside the shared folder. Fetch extra results so we can pick the correct one.
      first: isCrossTeam ? 10 : 1,
    },
    skip: shouldSkip,
    fetchPolicy: isCrossTeam ? 'network-only' : 'cache-and-network',
  });

  // Cross-team: when duplicate folder names exist (own + shared), pick the folder
  // from the target team. Without this, the query may return the user's own folder
  // (which lacks securityTeams for the user's team), causing a false "no access" error.
  const edges = data?.groups?.page?.edges;
  const currentGroup =
    isCrossTeam && edges?.length
      ? (edges.find((e) => e.node.teamId === effectiveTeamId)?.node ?? edges[0]?.node ?? null)
      : (edges?.[0]?.node ?? null);

  const refetch = useCallback(() => {
    if (!shouldSkip) apolloRefetch();
  }, [shouldSkip, apolloRefetch]);

  // When query is skipped (e.g., root path), we're not loading — return false
  return { currentGroup, loading: shouldSkip ? false : loading, refetch };
}
